import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Honeybadger from '@honeybadger-io/js';
import MemberContext from './MemberContext';
import SummaryContext from './SummaryContext';
import UserStateContext from './UserStateContext';
import CashApi from '../services/CashApi';
import {ACTION_REQUIRED, REJECTED} from '../constants';
import {getDaysUntilDate} from '../utils/dateUtils';
import noop from '../utils/noop';

const FIX_BANKING_INFO_STATUSES = [ACTION_REQUIRED, REJECTED];

const Context = createContext({
  bankAccount: null,
  fetchBankAccount: noop,
  setBankAccount: noop,
  isFetching: false,
  error: null,
  showBankInfoPrompt: false,
  dismissBankInfoPrompt: noop,
  needsBankingInfo: false,
  needsToFixBankingInfo: false,
});

export default Context;

const shouldDisplayBankInfo = (daysUntilWedding, bankInfoDismissed) => {
  if (!daysUntilWedding) return false;

  const isWeddingDayPassed = daysUntilWedding <= 0;

  if (!isWeddingDayPassed && !bankInfoDismissed) return true;

  const daysSinceDismissed = Math.floor(
    (new Date() - new Date(bankInfoDismissed)) / 8.64e7, // number of milliseconds in 1 day
  );

  if (!isWeddingDayPassed && daysSinceDismissed > 7) return true;

  return false;
};

export const BankAccountContextProvider = ({children}) => {
  // Contexts
  const {userStateData} = useContext(UserStateContext);
  const {member} = useContext(MemberContext);
  const {summary, isFetching: isFetchingSummary} = useContext(SummaryContext);

  // State
  const [bankAccount, setBankAccount] = useState(undefined);
  const [isFetching, setIsFetching] = useState(true);
  const [shouldDisplay, setShouldDisplay] = useState(null);
  const [errorState, setErrorState] = useState(false);

  const fetchBankAccount = useCallback(async ({refreshAccount} = {}) => {
    try {
      const response = await CashApi.getBankAccount({refreshAccount});

      if (response.ok) {
        setBankAccount(await response.json());
      } else if (!response.ok && response.status !== 404) {
        throw Error(response.statusText);
      } else if (!response.ok) {
        setBankAccount(null);
      }
    } catch (error) {
      Honeybadger.notify(error, 'FetchBankAccountError');

      setErrorState(error);
    }

    setIsFetching(false);
  }, []);

  // Effects
  useEffect(() => {
    if (bankAccount === undefined) {
      fetchBankAccount();
    }
  }, [fetchBankAccount, bankAccount]);

  useEffect(() => {
    if (!member.wedding_date || !userStateData) {
      return;
    }

    const determineShouldDisplay = async () => {
      const result = get(userStateData, 'bankInfoDismissed', false);
      const daysUntilWedding = getDaysUntilDate(member.wedding_date);
      const passedDisplayCriteria = shouldDisplayBankInfo(
        daysUntilWedding,
        result,
      );

      setShouldDisplay(passedDisplayCriteria);
    };

    determineShouldDisplay();
  }, [userStateData, member.wedding_date]);

  // Locals
  const needsBankingInfo =
    !bankAccount && !errorState && summary.totalCashFunds;
  const needsToFixBankingInfo =
    !!bankAccount &&
    FIX_BANKING_INFO_STATUSES.includes(bankAccount.stripeStatus);
  const showBankInfoPrompt = needsBankingInfo && shouldDisplay === true;
  const isDeterminingBankInfoPrompt =
    shouldDisplay === null || isFetching || isFetchingSummary;

  return (
    <Context.Provider
      value={{
        bankAccount,
        fetchBankAccount,
        setBankAccount,
        isFetching,
        error: errorState,
        dismissBankInfoPrompt: () => setShouldDisplay(false),
        needsBankingInfo,
        needsToFixBankingInfo,
        showBankInfoPrompt: !isDeterminingBankInfoPrompt && showBankInfoPrompt,
        isDeterminingBankInfoPrompt,
      }}>
      {children}
    </Context.Provider>
  );
};

BankAccountContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
